import { useParams, Link, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { article, breadcrumb } from '@/lib/seo';
import { ARTICLES, getArticleBody } from '@/mocks/articlesData';

export default function ArticlePage() {
  const { slug = '' } = useParams();
  const meta = ARTICLES[slug];

  useDocumentHead(
    meta
      ? {
          title: meta.title,
          description: meta.description,
          ogType: 'article',
          jsonLd: [
            article({ headline: meta.h1, description: meta.description, path: `/blogue/${slug}`, image: 'https://nuisibles-guadeloupe.fr' + meta.hero }),
            breadcrumb([
              { name: 'Accueil', path: '/' },
              { name: 'Blogue', path: '/blogue' },
              { name: meta.crumb, path: `/blogue/${slug}` },
            ]),
          ],
        }
      : { title: 'Article introuvable — Gwada Nuisibles', description: '', noindex: true }
  );

  if (!meta) return <Navigate to="/blogue" replace />;

  const body = getArticleBody(slug);

  return (
    <main>
      <Header />

      <Breadcrumb items={[{ label: 'Accueil', href: '/' }, { label: 'Blogue', href: '/blogue' }, { label: meta.crumb }]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden min-h-[300px] md:min-h-[380px] flex items-end">
        <div className="absolute inset-0">
          <img src={meta.hero} alt={meta.h1} className="w-full h-full object-cover object-center" loading="eager" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.92), rgba(11,58,46,0.72) 55%, rgba(11,58,46,0.5))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.5), transparent 45%, transparent 70%, rgba(11,58,46,0.45))' }} />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          <h1 className="font-heading font-black text-2xl md:text-4xl text-white leading-tight max-w-3xl">{meta.h1}</h1>
        </div>
      </section>

      {/* CORPS */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="article-prose">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href = '', children }) =>
                  href.startsWith('/') ? (
                    <Link to={href} className="text-secondary-600 font-semibold underline decoration-secondary-300 hover:text-secondary-700">{children}</Link>
                  ) : (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-secondary-600 font-semibold underline">{children}</a>
                  ),
              }}
            >
              {body}
            </ReactMarkdown>
          </div>

          {/* NOTRE SOLUTION */}
          <div className="mt-12 rounded-2xl bg-primary-900 text-white p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl mb-3">Notre solution</h2>
            <p className="text-white/85 leading-relaxed mb-5">
              Nos techniciens interviennent à domicile partout en Guadeloupe. Diagnostic clair, prix ferme annoncé avant intervention.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={meta.serviceHref} className="inline-flex items-center gap-2 bg-white text-primary-900 px-5 py-3 rounded-lg font-bold text-sm hover:bg-background-50 transition-colors">{meta.serviceLabel}</Link>
              <Link to="/tarifs" className="inline-flex items-center gap-2 border-2 border-white/50 text-white px-5 py-3 rounded-lg font-semibold text-sm hover:bg-white/10 transition-colors">Voir nos tarifs</Link>
            </div>
          </div>

          {/* CTA WHATSAPP */}
          <div className="mt-8 text-center">
            <p className="font-heading font-bold text-lg text-foreground-950 mb-4">Un doute ? Envoyez une photo sur WhatsApp, on vous répond en 2 h.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>

          {/* À LIRE AUSSI */}
          {meta.related?.length ? (
            <div className="mt-12 border-t border-background-200 pt-8">
              <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4">À lire aussi</h2>
              <ul className="space-y-2">
                {meta.related.map((r) => (
                  <li key={r.href}>
                    <Link to={r.href} className="text-secondary-600 font-semibold hover:text-secondary-700">→ {r.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </section>

      <ContactFormBlock />
      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
